import {
  type AlternativeFuelRatesResponse,
  type QuarterlyForecastAlternativeFuelRatesHierarchy,
  type MtmYtmRequestPayload,
  formatDateUtcToTimezoneDateOnly,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {Iso8601} from '@hconnect/uikit'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

const getAlternativeFuelRatesMtmYtmUrlSuffix = (showYTM?: boolean) =>
  showYTM ? 'alternative-fuel-rates-ytm' : 'alternative-fuel-rates-mtm'

export const getAlternativeFuelRatesMtmYtmQueryKeys = (requestPayload: MtmYtmRequestPayload) => [
  getAlternativeFuelRatesMtmYtmUrlSuffix(requestPayload.showYTM),
  formatDateUtcToTimezoneDateOnly(requestPayload.timestamp, requestPayload.timezone),
  requestPayload.locationId
]

export const getAlternativeFuelRates = async (
  payload: MtmYtmRequestPayload,
  signal?: AbortSignal
) => {
  const {timestamp, timezone, locationId} = payload

  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)
  const url = `/kpi/dashboard/${getAlternativeFuelRatesMtmYtmUrlSuffix(payload.showYTM)}`

  const response = await ApiClient.axiosInstance.get<AlternativeFuelRatesResponse>(url, {
    params: {locationId, date: referenceDate},
    signal
  })

  return response.data
}

export const useAlternativeFuelRates = <T>(
  payload: MtmYtmRequestPayload,
  options?: Omit<
    UseQueryOptions<AlternativeFuelRatesResponse, AxiosError, T>,
    'queryKey' | 'queryFn'
  >
) => {
  const QueryKeys = getAlternativeFuelRatesMtmYtmQueryKeys(payload)

  return useQuery<AlternativeFuelRatesResponse, AxiosError, T>({
    queryKey: QueryKeys,
    queryFn: async ({signal}) => {
      return getAlternativeFuelRates(payload, signal)
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}

export const useAlternativeFuelRatesQuarterly = (
  timestamp: Iso8601,
  timezone: string,
  locationId?: string,
  options?: Omit<
    UseQueryOptions<QuarterlyForecastAlternativeFuelRatesHierarchy | null, AxiosError>,
    'queryKey' | 'queryFn'
  >
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)

  return useQuery<QuarterlyForecastAlternativeFuelRatesHierarchy | null, AxiosError>({
    queryKey: ['alternative-fuel-rates-quarterly', locationId, referenceDate],
    queryFn: async ({signal}) => {
      if (!locationId) return Promise.resolve(null)

      const response =
        await ApiClient.axiosInstance.get<QuarterlyForecastAlternativeFuelRatesHierarchy>(
          '/kpi/dashboard/alternative-fuel-rates-qf',
          {
            params: {locationId, timestamp: referenceDate},
            signal
          }
        )
      return response.data
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}
